import {
  isSkillEditDefinitelyBarred,
  stashSkillDraft,
} from "@/shared/domain/skills";
import { RobotoNotFoundException } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "./approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import { coerceToSkillDraft, type CreateSkillInput } from "./createSkillCoerce";
import { CreateSkillConfirmation } from "./CreateSkillConfirmation";

// `coerceToSkillDraft` lives in `createSkillCoerce` to stay out of the
// tool ⇄ confirmation import cycle; re-exported here so the co-located test
// can import the normalization contract alongside `tool`.
export { coerceToSkillDraft, type CreateSkillInput };

// ---------------------------------------------------------------------------
// JSON Schema
// ---------------------------------------------------------------------------

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    name: {
      type: "string",
      description:
        "Short slug-style name for the skill (required to create, ignored on " +
        "update). Letters, digits, hyphens, and underscores only — no spaces. " +
        "Must be unique in the org.",
    },
    description: {
      type: "string",
      description:
        "The 'when to use' text (required to create, <=500 chars): a one-line " +
        "summary of when the AI should reach for this skill. Shown in the " +
        "skill registry.",
    },
    body: {
      type: "string",
      description:
        "The procedure itself (required to create): the full instructions the AI follows " +
        "when the skill is invoked. May be multi-line / Markdown. Wrap each data " +
        "topic the procedure investigates in double brackets — [[/imu/data]], " +
        "[[vehicle_attitude]]. To point at a specific field, append its " +
        "dot-delimited message path after the topic name: " +
        "[[/imu/data.angular_velocity.x]], [[vehicle_attitude.pitch]]. These are " +
        "named Topics from the Roboto data model, not general subject matters. " +
        "The topic names (field suffix excluded) become the skill's relevant " +
        "topics, whose full schemas are preloaded when the skill is invoked " +
        "with a dataset in scope.",
    },
    accessibility: {
      type: "string",
      enum: ["private", "org", "org-editable"],
      description:
        "Who can see / edit the skill. 'private' (default): only the author. " +
        "'org': visible to the whole org, author-only edits. 'org-editable': " +
        "visible to the org and editable by any subscribed member.",
    },
    tags: {
      type: "array",
      items: { type: "string" },
      description:
        "Optional free-form labels for categorization (e.g. 'qa-review', " +
        "'triage'). On an update these are ADDED to the skill's existing tags, " +
        "never a replacement for them — listing a tag it already has is " +
        "harmless. Tags alone are a complete update: to just retag a skill, " +
        "send target_skill_name and tags with no description or body.",
    },
    remove_tags: {
      type: "array",
      items: { type: "string" },
      description:
        "Tags to take off the skill. Update only — ignored without " +
        "target_skill_name, since a new skill has no tags to remove. Naming a " +
        "tag the skill doesn't have is harmless. A tag listed in both 'tags' " +
        "and 'remove_tags' is added, not removed. Like 'tags', this alone is " +
        "a complete update.",
    },
    // One line by design — the update semantics live in the tool
    // description, and every spec is re-sent on every thread request.
    target_skill_name: {
      type: "string",
      description:
        "Name of an existing skill to update instead of creating a new one, " +
        "as it appears in the skill registry (a trailing '(private)' / " +
        "'(org)' suffix is ignored). Omit to create.",
    },
  },
  // No top-level `required` key: an update (target_skill_name set) replaces
  // only the content it supplies and ignores `name` entirely, so a static
  // `required` would compel the model to invent values that go nowhere. A
  // schema can't express "required only when target_skill_name is absent"
  // without a conditional the Bedrock Converse validator may reject, so
  // create-mode's requirement is enforced in `execute` instead — matching the
  // sibling `client_create_agent`.
};

// ---------------------------------------------------------------------------
// Execute
//
// Unlike most tools, this one does NOT persist. It stashes a draft and
// navigates the user to the Skills page, which opens the create editor
// pre-filled on a create and the named skill's editor on an update —
// mirroring `client_create_agent`. Nothing is written until the user clicks
// Save there, so the editor (not this tool) is the final validation seam.
//
// An update still verifies the target here rather than after navigating: a
// name the model invented, or a skill the caller may not edit, has to come
// back as a `toolError` the model can act on, not as an error page the user
// discovers on arrival.
// ---------------------------------------------------------------------------

/**
 * Confirm a named skill exists and isn't plainly read-only, returning a
 * `toolError` to relay when it can't be updated and `null` when the handoff
 * should proceed.
 *
 * Only definitive rejections are made here. `org-editable` editability turns
 * on the caller's subscription, which the by-name lookup doesn't report, so
 * that case proceeds and the Skills page settles it with the full summary —
 * a false rejection would be worse than a late one.
 */
async function rejectUnusableUpdateTarget(
  targetSkillName: string,
  deps: ClientToolDeps,
): Promise<ClientToolExecutionResult | null> {
  if (!deps.skillService || !deps.userId) {
    // Nothing to verify against. Proceed rather than block: the Skills page
    // resolves the name again and reports failure to the user.
    return null;
  }
  try {
    const skill = await deps.skillService.getSkillByName(targetSkillName, {
      orgId: deps.orgId,
    });
    if (isSkillEditDefinitelyBarred(skill, deps.userId)) {
      return toolError(
        `The skill "${targetSkillName}" is org-visible but only its author ` +
          "can change it. Suggest the user ask the author, or create a " +
          "separate skill instead.",
        { target_skill_name: targetSkillName },
      );
    }
    return null;
  } catch (error) {
    if (error instanceof RobotoNotFoundException) {
      return toolError(
        `No skill named "${targetSkillName}" is visible in this org, so ` +
          "there is nothing to update. Check the name against the skill " +
          "registry, or omit target_skill_name to create a new skill.",
        { target_skill_name: targetSkillName },
      );
    }
    // Existence unknown. Don't imply the name is wrong, and don't invite a
    // create that would collide if the skill does exist.
    return toolError(
      `Could not look up the skill named "${targetSkillName}", so it is ` +
        "unknown whether it exists. Report the failure and let the user retry.",
      { target_skill_name: targetSkillName },
    );
  }
}

async function execute(
  input: CreateSkillInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  const draft = coerceToSkillDraft(input);
  if (draft === null) {
    return toolError(
      "A skill draft needs at least a name, a description, or a body.",
    );
  }
  const targetSkillName = draft.targetSkillName;
  if (targetSkillName !== undefined) {
    const rejection = await rejectUnusableUpdateTarget(targetSkillName, deps);
    if (rejection !== null) {
      return rejection;
    }
  }
  stashSkillDraft(draft);
  deps.goto.navigateTo(Goto.route.AgentSkills);
  return targetSkillName !== undefined
    ? toolSuccess("opened_skill_editor_for_update", {
        navigated_to: Goto.route.AgentSkills,
        mode: "update",
        target_skill_name: targetSkillName,
      })
    : toolSuccess("opened_skill_editor_with_draft", {
        navigated_to: Goto.route.AgentSkills,
        mode: "create",
        name: draft.name ?? null,
      });
}

// ---------------------------------------------------------------------------
// Tool module
// ---------------------------------------------------------------------------

export const tool = createClientTool<CreateSkillInput>({
  name: "client_create_skill",
  description:
    "Open the Skills editor pre-filled with a drafted AI Skill — a stored, " +
    "versioned procedure the AI can invoke later — based on the current " +
    "conversation. Handles BOTH creating a new skill and updating an existing " +
    "one. To CREATE: provide a slug-style 'name', a 'description' (when to use " +
    "it), and a 'body' (the procedure). To UPDATE an existing skill: pass its " +
    "name as 'target_skill_name' along with any of 'description' / 'body' / " +
    "'tags' / 'remove_tags'. Description and body full-replace the current " +
    "version's content in the editor for the user to review (old content stays " +
    "recoverable via version history); 'tags' are added to the skill's existing " +
    "ones and 'remove_tags' are taken off. Any one of them is enough — " +
    "retagging alone is a valid update. " +
    "'name' is ignored on an update, so it cannot rename a skill, and the tool " +
    "returns an error rather than creating anything if no skill has that name " +
    "or the user cannot edit it. Use " +
    "this when the user asks to save the current workflow as a skill, to create " +
    "one, or to change / improve an existing skill. In every case the user " +
    "lands in the editor to review, refine, and save — this tool does NOT " +
    "persist anything.",
  input_schema: inputSchema,
  execute,
  // Navigation-only (no persistence), so follow the user's Auto toggle by
  // default — same as the sibling `client_create_agent` draft tool.
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open skill editor with draft",
  displayDescription:
    "Opens the Skills editor pre-filled with a drafted skill, or with replacement content for one of your existing skills. Nothing is saved until you click Save in the editor.",
  ConfirmationContent: CreateSkillConfirmation,
});
